import type { ComponentChildren } from 'preact';
import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut';
import { ActionButton, PrimaryActionButton } from './ActionButton';

/**
 * Three-mode confirmation overlay:
 *
 *   • mode="simple" - title + body + Cancel/Confirm. The 80% case.
 *   • mode="typed"  - user must type `typedToken` exactly before
 *                     Confirm enables. Reserved for destructive
 *                     merges/deletes.
 *   • mode="form"   - children render a form above the action row.
 *                     Caller validates via `confirmDisabled`.
 *
 * Focus-trapped, Esc closes, backdrop closes, role="dialog". Confirm
 * supports `busy` so callers can disable the row while the request is
 * in flight (and re-enable on settle).
 */
export type ConfirmModalMode = 'simple' | 'typed' | 'form';

interface ConfirmModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void | Promise<void>;
    title: string;
    body?: ComponentChildren;
    children?: ComponentChildren;
    mode?: ConfirmModalMode;
    typedToken?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmVariant?: 'default' | 'danger';
    confirmDisabled?: boolean;
    busy?: boolean;
}

export function ConfirmModal({
    open,
    onClose,
    onConfirm,
    title,
    body,
    children,
    mode = 'simple',
    typedToken,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    confirmVariant = 'default',
    confirmDisabled = false,
    busy = false,
}: ConfirmModalProps) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const [typedValue, setTypedValue] = useState('');
    // Per-instance id so two modals open simultaneously don't collide
    // on the ARIA labelledby reference.
    const titleId = useId();

    useFocusTrap(dialogRef, open);
    useKeyboardShortcut('Escape', () => open && onClose(), [open, onClose]);

    // Reset the typed-token field whenever the dialog re-opens, so a
    // previous "MERGE" entry doesn't leak between confirmations.
    useEffect(() => {
        if (open) setTypedValue('');
    }, [open]);

    if (!open) return null;

    const typedOk = mode !== 'typed' || (typedToken !== undefined && typedValue === typedToken);
    const isConfirmDisabled = busy || confirmDisabled || !typedOk;

    return (
        <div
            class="fixed inset-0 z-50 flex items-center justify-center bg-surface-base/80 p-4 backdrop-blur-sm"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
            role="presentation"
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                class="w-full max-w-md rounded-md border border-rule bg-surface-raised p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <h2
                    id={titleId}
                    class="font-display text-[24px] italic leading-tight text-fg-primary"
                >
                    {title}
                </h2>

                {body && (
                    <div class="mt-3 text-sm text-fg-secondary">{body}</div>
                )}

                {mode === 'form' && children && (
                    <div class="mt-5 space-y-3">{children}</div>
                )}

                {mode === 'typed' && typedToken && (
                    <div class="mt-5 space-y-2">
                        <label class="block font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                            Type <span class="text-state-failed">{typedToken}</span> to continue
                        </label>
                        <input
                            type="text"
                            value={typedValue}
                            onInput={(e) =>
                                setTypedValue((e.target as HTMLInputElement).value)
                            }
                            class="w-full rounded border border-rule bg-surface-base px-3 py-2 font-mono text-sm text-fg-primary outline-none focus:border-accent"
                            autoFocus
                        />
                    </div>
                )}

                <div class="mt-6 flex items-center justify-end gap-2">
                    <ActionButton onClick={onClose} disabled={busy}>
                        {cancelLabel}
                    </ActionButton>
                    <PrimaryActionButton
                        variant={confirmVariant}
                        busy={busy}
                        disabled={isConfirmDisabled}
                        onClick={() => {
                            void onConfirm();
                        }}
                    >
                        {confirmLabel}
                    </PrimaryActionButton>
                </div>
            </div>
        </div>
    );
}
